import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, type LoginInput } from "@wakaskul/shared";
import { BookOpen, Eye, EyeOff, Mail, Phone } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import type { z } from "zod";

import { ApiError } from "../lib/api";
import { landingPath, useLogin } from "../lib/session";
import { OfficialWordmark } from "../components/OfficialWordmark";

/**
 * Signing in to one school.
 *
 * The card is a page from a cash book: ruled rows, a mono label in the
 * description column, the control in the amount column, and a confirm bar
 * carrying the same gold a balance row carries everywhere else in the product.
 * That is the metaphor the whole system is built on, and this is the first
 * screen anyone sees, so it is worth stating here rather than after sign-in.
 *
 * The page also names the address it is signing you in to, because the school
 * is resolved from the subdomain rather than chosen from a list. Someone who
 * reaches the wrong school's URL gets "wrong phone number or password" from an
 * account that is perfectly valid somewhere else, and would otherwise have
 * nothing on screen to tell them why. The host is the only piece of school
 * context that exists before anyone authenticates.
 */

/** What the user types, before Zod normalises it. */
type LoginForm = z.input<typeof loginSchema>;

/**
 * The API refuses a 2FA account without a code, and says so in the problem
 * title. Matching on the sentence is fragile and should become a typed error
 * code when the enrolment flow lands (`auth.service.ts` notes it as pending),
 * but leaving it unhandled is worse: today a user with `twoFactorEnabled` set
 * is told to enter a code by a form with nowhere to enter it.
 */
function asksForCode(error: ApiError): boolean {
  return error.status === 401 && /6-digit/i.test(error.problem.title ?? "");
}

export function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const [needsCode, setNeedsCode] = useState(false);
  const [method, setMethod] = useState<"phone" | "email">("phone");

  const {
    register,
    handleSubmit,
    resetField,
    setError,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm, unknown, LoginInput>({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const result = await login.mutateAsync(values);
      navigate(landingPath(result.user), { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        if (asksForCode(error)) {
          setNeedsCode(true);
          setError("root", { message: error.problem.title });
          // The field does not exist until this render, so focus it after it does.
          window.setTimeout(() => setFocus("totpCode"), 0);
          return;
        }

        // Field-level problems land on their field; everything else on the form.
        const fields = error.fieldErrors;
        const named = Object.keys(fields);
        if (named.length > 0) {
          for (const field of named) {
            setError(field as keyof LoginForm, { message: fields[field]?.[0] });
          }
        } else {
          setError("root", { message: error.problem.title });
        }
        return;
      }
      setError("root", { message: "Could not reach the server. Check your connection." });
    }
  });

  return (
    <main className="h-[100dvh] overflow-hidden bg-[#edf2e9] text-[#12201f]">
      <div className="mx-auto flex h-full max-w-[1440px] flex-col p-5 sm:p-7 lg:p-[50px]">
        <header className="flex items-center justify-between gap-4">
          <Link to="/" aria-label="Wakaskul home">
            <OfficialWordmark className="h-8" />
          </Link>
          <span className="font-mono text-[0.55rem] uppercase tracking-[0.18em] text-[#156064]/65">School workspace</span>
        </header>

        <div className="grid min-h-0 flex-1 items-center gap-6 py-4 lg:grid-cols-[minmax(0,1fr)_minmax(24rem,31rem)] lg:gap-12">
          <section className="hidden min-w-0 lg:flex lg:flex-col lg:justify-center">
            <p className="font-mono text-[0.65rem] font-bold uppercase tracking-[0.22em] text-[#156064]">Your school workspace</p>
            <h1 className="mt-4 max-w-lg font-display text-5xl font-bold leading-[0.95] tracking-[-0.055em] xl:text-6xl">
              School work, made clear.
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-[#12201f]/60">
              Sign in to manage people, learning and fees.
            </p>
            <img
              src="/images/login/login-school-illustration.png"
              alt="School staff coordinating learners, classes and parent communication"
              className="mt-6 h-auto max-h-[380px] w-full max-w-[620px] object-contain object-left-bottom"
            />
          </section>

          <div className="flex w-full items-center justify-center lg:justify-end">
            <form
              onSubmit={onSubmit}
              noValidate
              className="max-h-full w-full overflow-hidden rounded border border-[#c9d5c7] bg-white shadow-[0_24px_70px_rgba(18,32,31,0.14)]"
            >
              <div className="flex items-center justify-between gap-4 border-b border-[#dce5d9] px-5 py-4 sm:px-7">
                <div>
                  <h2 className="font-display text-2xl font-bold text-[#12201f]">Sign in</h2>
                  <p className="mt-1 text-sm text-[#12201f]/55">Use your school account.</p>
                </div>
                <BookOpen className="h-5 w-5 text-[#156064]" aria-hidden="true" />
              </div>

              <div className="grid grid-cols-2 border-b border-[#dce5d9] p-2">
                <button
                  type="button"
                  onClick={() => {
                    resetField("email");
                    setMethod("phone");
                  }}
                  className={`flex items-center justify-center gap-2 rounded px-3 py-2.5 text-sm font-semibold transition ${method === "phone" ? "bg-[#156064] text-white" : "text-[#12201f]/55 hover:bg-[#edf2e9] hover:text-[#12201f]"}`}
                  aria-pressed={method === "phone"}
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  Phone
                </button>
                <button
                  type="button"
                  onClick={() => {
                    resetField("phone");
                    setMethod("email");
                  }}
                  className={`flex items-center justify-center gap-2 rounded px-3 py-2.5 text-sm font-semibold transition ${method === "email" ? "bg-[#156064] text-white" : "text-[#12201f]/55 hover:bg-[#edf2e9] hover:text-[#12201f]"}`}
                  aria-pressed={method === "email"}
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  Email
                </button>
              </div>

              {method === "phone" ? (
                <Row htmlFor="phone" label="Phone" hint="Use your school phone." error={errors.phone?.message}>
                  <input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="username"
                    autoFocus
                    placeholder="0771 234 567"
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={errors.phone ? "phone-error" : "phone-hint"}
                    className={inputClass(Boolean(errors.phone))}
                    {...register("phone")}
                  />
                </Row>
              ) : (
                <Row htmlFor="email" label="Email" hint="Use your school email." error={errors.email?.message}>
                  <input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    autoFocus
                    placeholder="you@school.org"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : "email-hint"}
                    className={inputClass(Boolean(errors.email))}
                    {...register("email")}
                  />
                </Row>
              )}

              <Row htmlFor="password" label="Password" error={errors.password?.message}>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    aria-invalid={Boolean(errors.password)}
                    aria-describedby={errors.password ? "password-error" : undefined}
                    className={`${inputClass(Boolean(errors.password))} pr-12`}
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r text-[#12201f]/45 hover:text-[#12201f]"
                  >
                    {showPassword ? <EyeOff className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" /> : <Eye className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />}
                  </button>
                </div>
              </Row>

              {needsCode && (
                <Row htmlFor="totpCode" label="Code" hint="Enter your 6-digit code." error={errors.totpCode?.message}>
                  <input
                    id="totpCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    aria-invalid={Boolean(errors.totpCode)}
                    aria-describedby={errors.totpCode ? "totpCode-error" : "totpCode-hint"}
                    className={`${inputClass(Boolean(errors.totpCode))} tabular tracking-[0.3em]`}
                    {...register("totpCode")}
                  />
                </Row>
              )}

              {errors.root && (
                <div className="border-b border-[#dce5d9] px-5 py-4 sm:px-7">
                  <p role="alert" className="rounded border-l-[3px] border-[#c85b4b] bg-[#c85b4b]/10 px-3.5 py-3 text-sm leading-relaxed text-[#12201f]">
                    {errors.root.message}
                  </p>
                </div>
              )}

              <div className="p-5 sm:p-5">
                <button type="submit" disabled={isSubmitting} className="flex w-full items-center justify-between gap-4 rounded bg-[#156064] px-5 py-3.5 text-left font-display font-bold text-white shadow-[0_12px_28px_rgba(21,96,100,0.16)] transition hover:bg-[#0e4e4d] disabled:opacity-60">
                  <span className="font-mono text-[0.55rem] uppercase tracking-[0.16em]">{isSubmitting ? "Working" : "Continue"}</span>
                  <span className="text-lg">{isSubmitting ? "Signing in…" : "Sign in"}<span aria-hidden="true"> →</span></span>
                </button>
                <div className="mt-5 flex flex-col gap-2">
                  <p className="text-sm leading-relaxed text-[#12201f]/55">Need a reset? Ask your school administrator. <Link to="/demo" className="font-medium text-[#156064] hover:underline">View demo</Link>.</p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────

function inputClass(invalid: boolean, dark = false): string {
  if (dark) {
    return `w-full rounded-xl border px-3.5 py-3 text-white outline-none placeholder:text-white/35 ${
      invalid ? "border-[#e77a6b] bg-[#e77a6b]/10" : "border-white/10 bg-white/5 focus:border-[#e9b45b]"
    }`;
  }
  return `w-full rounded border bg-raised px-3.5 py-3 text-ink outline-none placeholder:text-ink-faint ${
    invalid ? "border-bad" : "border-rule focus:border-accent"
  }`;
}

/**
 * One ruled line: the label sits in the description column, the control in the
 * amount column. The columns collapse below `sm`, because a 6.5rem label
 * column on a narrow phone leaves the input too little room to be typed into
 * comfortably.
 */
function Row({
  htmlFor,
  label,
  hint,
  error,
  dark = false,
  children,
}: {
  htmlFor: string;
  label: string;
  hint?: string;
  error?: string;
  dark?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-1.5 border-b px-5 py-3 sm:grid sm:grid-cols-[6.5rem_1fr] sm:items-center sm:gap-3 ${dark ? "border-white/10" : "border-rule-soft"}`}>
      <label
        htmlFor={htmlFor}
        className={`font-mono text-[0.6rem] uppercase tracking-[0.16em] sm:self-center ${dark ? "text-white/45" : "text-ink-faint"}`}
      >
        {label}
      </label>
      <div className="flex min-w-0 flex-col gap-1.5">
        {children}
        {error ? (
          <p id={`${htmlFor}-error`} className={`text-sm ${dark ? "text-[#e77a6b]" : "text-bad"}`}>
            {error}
          </p>
        ) : (
          hint && (
            <p id={`${htmlFor}-hint`} className={`text-xs ${dark ? "text-white/40" : "text-ink-faint"}`}>
              {hint}
            </p>
          )
        )}
      </div>
    </div>
  );
}
